import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  CircleDashed,
  HelpCircle,
  LayoutDashboard,
  Link2,
  Sheet as SheetIcon,
  Users,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ReportSheet } from "@/components/report/report-sheet";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { StoryRefDialog } from "@/components/shared/story-ref-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useReport } from "@/hooks/use-report";
import { useCreateShareToken } from "@/hooks/use-share";
import { type TriageItemRow, useTriageItems } from "@/hooks/use-triage";
import { cn } from "@/lib/utils";

const REPORT_TITLES = {
  sales: "Proposal Brief",
  discovery: "Scope Brief",
  build: "Change Log",
} as const;

export function ReportPage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const { stage } = useSearch({ from: "/s/$solutionId" });
  const reportSearch = useSearch({ from: "/s/$solutionId/report" });
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useReport(solutionId);
  const triage = useTriageItems(solutionId, {});
  const [openStoryId, setOpenStoryId] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const createToken = useCreateShareToken(solutionId);

  const title = REPORT_TITLES[stage] ?? REPORT_TITLES.discovery;

  function setView(view: "dashboard" | "sheet") {
    navigate({
      to: "/s/$solutionId/report",
      params: { solutionId },
      search: { view },
    });
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8 space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-8">
        <ErrorState message="Failed to load report" onRetry={() => refetch()} />
      </div>
    );
  }

  const viewToggle = (
    <div className="inline-flex items-center rounded-md border border-[var(--color-border)] bg-card p-0.5 h-9">
      <button
        type="button"
        onClick={() => setView("dashboard")}
        className={cn(
          "inline-flex h-8 px-3 items-center gap-1.5 rounded-[5px] text-xs font-medium transition-colors",
          reportSearch.view === "dashboard"
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <LayoutDashboard className="h-3.5 w-3.5" />
        Dashboard
      </button>
      <button
        type="button"
        onClick={() => setView("sheet")}
        className={cn(
          "inline-flex h-8 px-3 items-center gap-1.5 rounded-[5px] text-xs font-medium transition-colors",
          reportSearch.view === "sheet"
            ? "bg-muted text-foreground"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        <SheetIcon className="h-3.5 w-3.5" />
        Sheet
      </button>
    </div>
  );

  if (reportSearch.view === "sheet") {
    return (
      <article className="mx-auto max-w-7xl px-6 py-8 space-y-6">
        <PageHeader
          title={title}
          description="Shareable summary of change requests. Generate a share link for stakeholders to view and comment without signing in."
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)}>
                <Link2 className="h-3.5 w-3.5 mr-1.5" />
                Generate share link
              </Button>
              {viewToggle}
            </div>
          }
        />
        <ReportSheet
          solutionName={data.solution.name}
          clientName={data.solution.clientName}
          reportTitle={title}
          items={triage.data?.data}
          isLoading={triage.isLoading}
          onOpenStory={(id) => setOpenStoryId(id)}
          hideShareButton
        />
        <StoryRefDialog
          storyId={openStoryId}
          onOpenChange={(open) => !open && setOpenStoryId(null)}
        />
        <ShareLinkDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          createToken={createToken}
        />
      </article>
    );
  }

  return (
    <article className="mx-auto max-w-7xl px-6 py-8 space-y-6 print:max-w-none">
      <PageHeader
        title={title}
        description={`${data.solution.clientName} · ${data.solution.name}`}
        actions={<div className="flex items-center gap-2 print:hidden">{viewToggle}</div>}
      />

      <Scoreboard
        solutionId={solutionId}
        items={triage.data?.data ?? []}
        loading={triage.isLoading}
        questionsCount={data.questions.length}
        conflictsCount={data.conflicts.length}
        sourcesCount={new Set((triage.data?.data ?? []).map((it) => it.sourceId)).size}
        stakeholdersCount={data.stakeholders.length}
      />

      <ShareLinkDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        createToken={createToken}
      />
    </article>
  );
}

type DrillFilter =
  | "in-scope"
  | "in-review"
  | "out-of-scope"
  | "deferred"
  | "change-order"
  | "roadmap"
  | "reject"
  | null;

function Scoreboard({
  solutionId,
  items,
  loading,
  questionsCount,
  conflictsCount,
  sourcesCount,
  stakeholdersCount,
}: {
  solutionId: string;
  items: TriageItemRow[];
  loading: boolean;
  questionsCount: number;
  conflictsCount: number;
  sourcesCount: number;
  stakeholdersCount: number;
}) {
  const [filter, setFilter] = useState<DrillFilter>(null);

  const counts = useMemo(() => buildCounts(items), [items]);

  function toggle(next: DrillFilter) {
    setFilter((cur) => (cur === next ? null : next));
  }

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-28 rounded-xl" />
        ))}
      </div>
    );
  }

  const heroes: HeroTile[] = [
    {
      filter: "in-scope",
      label: "In scope",
      count: counts.inScope,
      total: counts.total,
      tint: "success",
    },
    {
      filter: "in-review",
      label: "In review",
      count: counts.inReview,
      total: counts.total,
      tint: "warning",
    },
    {
      filter: "out-of-scope",
      label: "Out of scope",
      count: counts.outOfScope,
      total: counts.total,
      tint: "danger",
    },
    {
      filter: "deferred",
      label: "Deferred",
      count: counts.deferred,
      total: counts.total,
      tint: "info",
    },
  ];

  const oosTiles: HeroTile[] = [
    {
      filter: "change-order",
      label: "Change order",
      count: counts.changeOrder,
      total: counts.outOfScope || 1,
      tint: "warning",
    },
    {
      filter: "roadmap",
      label: "Roadmap / backlog",
      count: counts.roadmap,
      total: counts.outOfScope || 1,
      tint: "info",
    },
    {
      filter: "reject",
      label: "Rejected",
      count: counts.reject,
      total: counts.outOfScope || 1,
      tint: "danger",
    },
  ];

  const drillItems = filter ? filterItems(items, filter) : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {heroes.map((h) => (
          <HeroCard
            key={h.filter}
            tile={h}
            active={filter === h.filter}
            onClick={() => toggle(h.filter)}
          />
        ))}
      </div>

      <section className="rounded-xl border border-[var(--color-border)] bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-sm font-semibold">Out-of-scope breakdown</h2>
          <span className="text-xs text-muted-foreground tabular-nums">
            {counts.outOfScope} item{counts.outOfScope === 1 ? "" : "s"}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {oosTiles.map((t) => (
            <SubTile
              key={t.filter}
              tile={t}
              active={filter === t.filter}
              onClick={() => toggle(t.filter)}
            />
          ))}
        </div>
      </section>

      {filter && (
        <DrillList
          solutionId={solutionId}
          filter={filter}
          items={drillItems}
          onClose={() => setFilter(null)}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link
          to="/s/$solutionId/intake"
          params={{ solutionId }}
          className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-card p-4 hover:border-nymbl-blue hover:shadow-sm transition-all"
        >
          <SummaryCardInner
            icon={<CircleDashed className="h-4 w-4" />}
            label="Sources"
            count={sourcesCount}
          />
        </Link>
        <Link
          to="/s/$solutionId/intake"
          params={{ solutionId }}
          className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-card p-4 hover:border-nymbl-blue hover:shadow-sm transition-all"
        >
          <SummaryCardInner
            icon={<Users className="h-4 w-4" />}
            label="Stakeholders"
            count={stakeholdersCount}
          />
        </Link>
        <Link
          to="/s/$solutionId/questions"
          params={{ solutionId }}
          className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-card p-4 hover:border-nymbl-blue hover:shadow-sm transition-all"
        >
          <SummaryCardInner
            icon={<HelpCircle className="h-4 w-4" />}
            label="Open questions"
            count={questionsCount}
          />
        </Link>
        <Link
          to="/s/$solutionId/conflicts"
          params={{ solutionId }}
          className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] bg-card p-4 hover:border-nymbl-blue hover:shadow-sm transition-all"
        >
          <SummaryCardInner
            icon={<AlertTriangle className="h-4 w-4" />}
            label="Open conflicts"
            count={conflictsCount}
          />
        </Link>
      </div>
    </div>
  );
}

interface CountBundle {
  total: number;
  inScope: number;
  inReview: number;
  outOfScope: number;
  deferred: number;
  pending: number;
  changeOrder: number;
  roadmap: number;
  reject: number;
}

function buildCounts(items: TriageItemRow[]): CountBundle {
  const c: CountBundle = {
    total: items.length,
    inScope: 0,
    inReview: 0,
    outOfScope: 0,
    deferred: 0,
    pending: 0,
    changeOrder: 0,
    roadmap: 0,
    reject: 0,
  };
  for (const it of items) {
    switch (it.effectiveStatus) {
      case "in-scope":
        c.inScope++;
        break;
      case "in-review":
        c.inReview++;
        break;
      case "out-of-scope": {
        c.outOfScope++;
        const reason = it.decision?.reason;
        if (reason === "change-order") c.changeOrder++;
        else if (reason === "roadmap") c.roadmap++;
        else if (reason === "reject") c.reject++;
        break;
      }
      case "deferred":
        c.deferred++;
        break;
      default:
        c.pending++;
    }
  }
  return c;
}

function filterItems(items: TriageItemRow[], filter: DrillFilter): TriageItemRow[] {
  if (!filter) return items;
  if (filter === "change-order" || filter === "roadmap" || filter === "reject") {
    return items.filter(
      (i) => i.effectiveStatus === "out-of-scope" && i.decision?.reason === filter,
    );
  }
  return items.filter((i) => i.effectiveStatus === filter);
}

interface HeroTile {
  filter: DrillFilter;
  label: string;
  count: number;
  total: number;
  tint: "success" | "warning" | "danger" | "info";
}

const TINTS: Record<HeroTile["tint"], { bg: string; bar: string; text: string }> = {
  success: {
    bg: "bg-success-bg/40 border-success/40",
    bar: "bg-success",
    text: "text-success",
  },
  warning: {
    bg: "bg-warning-bg/40 border-warning/40",
    bar: "bg-warning",
    text: "text-warning",
  },
  danger: {
    bg: "bg-[#FBEAEA] border-[#E69999] dark:bg-[#2a1414] dark:border-[#7a3030]",
    bar: "bg-[#C04848] dark:bg-[#d36a6a]",
    text: "text-[#8B2727] dark:text-[#f08e8e]",
  },
  info: {
    bg: "bg-info-bg/40 border-info/40",
    bar: "bg-info",
    text: "text-info",
  },
};

function HeroCard({
  tile,
  active,
  onClick,
}: {
  tile: HeroTile;
  active: boolean;
  onClick: () => void;
}) {
  const t = TINTS[tile.tint];
  const pct = tile.total > 0 ? Math.round((tile.count / tile.total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-xl border-2 p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm",
        t.bg,
        active && "ring-2 ring-offset-2 ring-foreground/20",
      )}
    >
      <div className={cn("text-[11px] uppercase tracking-[0.14em] font-semibold", t.text)}>
        {tile.label}
      </div>
      <div className="font-display text-3xl font-bold mt-1 tabular-nums">{tile.count}</div>
      <div className="mt-3 h-1.5 rounded-full bg-foreground/10 overflow-hidden">
        <div className={cn("h-full rounded-full", t.bar)} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11px] text-muted-foreground mt-1.5 tabular-nums">
        {pct}% of {tile.total}
      </div>
    </button>
  );
}

function SubTile({
  tile,
  active,
  onClick,
}: {
  tile: HeroTile;
  active: boolean;
  onClick: () => void;
}) {
  const t = TINTS[tile.tint];
  const pct = tile.total > 0 ? Math.round((tile.count / tile.total) * 100) : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-lg border p-4 text-left transition-all hover:shadow-sm",
        t.bg,
        active && "ring-2 ring-offset-1 ring-foreground/20",
      )}
    >
      <div className="flex items-baseline justify-between">
        <div className={cn("text-[11px] uppercase tracking-[0.14em] font-semibold", t.text)}>
          {tile.label}
        </div>
        <div className="font-display text-xl font-bold tabular-nums">{tile.count}</div>
      </div>
      <div className="mt-2 h-1 rounded-full bg-foreground/10 overflow-hidden">
        <div className={cn("h-full rounded-full", t.bar)} style={{ width: `${pct}%` }} />
      </div>
    </button>
  );
}

const FILTER_LABEL: Record<NonNullable<DrillFilter>, string> = {
  "in-scope": "In scope",
  "in-review": "In review",
  "out-of-scope": "Out of scope",
  deferred: "Deferred",
  "change-order": "Change orders",
  roadmap: "Roadmap / backlog",
  reject: "Rejected",
};

function DrillList({
  solutionId,
  filter,
  items,
  onClose,
}: {
  solutionId: string;
  filter: NonNullable<DrillFilter>;
  items: TriageItemRow[];
  onClose: () => void;
}) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-card overflow-hidden">
      <header className="flex items-center justify-between px-5 py-3 border-b border-[var(--color-border)] bg-muted/40">
        <h2 className="font-display text-sm font-semibold">
          {FILTER_LABEL[filter]}
          <span className="ml-2 text-xs text-muted-foreground font-normal">({items.length})</span>
        </h2>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </header>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-sm text-muted-foreground text-center">
          No items match this filter.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-border)]">
          {items.map((it) => (
            <li key={it.id}>
              <Link
                to="/s/$solutionId/review/$itemId"
                params={{ solutionId, itemId: it.id }}
                className="flex items-start gap-3 px-5 py-3 hover:bg-muted/40 transition-colors"
              >
                <span className="font-mono text-[11px] text-muted-foreground tabular-nums w-16 shrink-0 mt-0.5">
                  {it.externalKey ?? it.id.slice(0, 6)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground/90 truncate">{it.extracted}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    {it.source.externalKey ?? it.source.name}
                    {it.speaker && <> · {it.speaker.name}</>}
                    {it.mapStory && (
                      <>
                        {" · "}
                        {it.mapStory.code}
                      </>
                    )}
                  </div>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60 mt-1.5 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SummaryCardInner({
  icon,
  label,
  count,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
}) {
  return (
    <>
      <span className="text-muted-foreground">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold">
          {label}
        </div>
        <div className="font-display text-lg font-bold tabular-nums">{count}</div>
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" />
    </>
  );
}

function ShareLinkDialog({
  open,
  onOpenChange,
  createToken,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  createToken: ReturnType<typeof useCreateShareToken>;
}) {
  const [password, setPassword] = useState("");
  const [allowComments, setAllowComments] = useState(true);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [createdHasPassword, setCreatedHasPassword] = useState(false);

  // Reset form whenever the dialog opens fresh.
  useEffect(() => {
    if (open) {
      setPassword("");
      setAllowComments(true);
      setCreatedUrl(null);
      setCreatedHasPassword(false);
    }
  }, [open]);

  function generate() {
    createToken.mutate(
      {
        expiresInDays: 30,
        allowComments,
        password: password.trim() || null,
      },
      {
        onSuccess: (token) => {
          if (typeof window === "undefined") return;
          setCreatedUrl(`${window.location.origin}/r/${token.token}`);
          setCreatedHasPassword(token.isPasswordProtected);
        },
      },
    );
  }

  function copy() {
    if (!createdUrl) return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(createdUrl)
        .then(() => toast.success("Copied to clipboard"))
        .catch(() => fallbackCopy(createdUrl));
    } else {
      fallbackCopy(createdUrl);
    }
  }

  function fallbackCopy(value: string) {
    if (typeof document === "undefined") return;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    document.body.removeChild(ta);
    if (ok) toast.success("Copied to clipboard");
    else toast.error("Couldn't copy automatically — select the link and copy manually");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        {createdUrl ? (
          <>
            <DialogHeader>
              <DialogTitle>Share link created</DialogTitle>
              <DialogDescription>
                {createdHasPassword
                  ? "Recipients will be prompted for the password. Send the password through a separate channel — don't paste it in the same message as the link."
                  : "Anyone with this link can view the report and add comments. Expires in 30 days."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                readOnly
                value={createdUrl}
                onFocus={(e) => e.currentTarget.select()}
                onClick={(e) => e.currentTarget.select()}
                className="font-mono text-xs"
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Done
                </Button>
                <Button onClick={copy}>
                  <Link2 className="h-3.5 w-3.5 mr-1.5" />
                  Copy link
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Generate share link</DialogTitle>
              <DialogDescription>
                Creates a 30-day read-only link. Optional password gates access.
              </DialogDescription>
            </DialogHeader>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                generate();
              }}
            >
              <div>
                <label
                  htmlFor="share-password"
                  className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 block"
                >
                  Password
                  <span className="ml-2 normal-case tracking-normal text-muted-foreground/70 font-normal">
                    optional
                  </span>
                </label>
                <Input
                  id="share-password"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Leave blank for anyone with the link"
                  className="text-sm"
                  autoComplete="off"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Min 4 characters when set. You'll need to send this to recipients out-of-band.
                </p>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowComments}
                  onChange={(e) => setAllowComments(e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Allow viewers to add comments</span>
              </label>

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={createToken.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createToken.isPending || (password.length > 0 && password.length < 4)}
                >
                  {createToken.isPending ? "Generating…" : "Generate"}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
